import { Component, inject } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import {
  HLM_DIALOG_SHELL,
  HlmBtnDirective,
  HlmIconComponent,
  HlmInputDirective,
  HlmSelectComponent,
  HLM_FORM_FIELD,
  HlmSelectOption,
} from '../../ui';

export interface AddEmailDialogData {
  /** Domain list fetched from API — populates the right-side dropdown. */
  domains: string[];
  /** Pre-filled username/domain for edit flows; empty strings for add. */
  initialUsername?: string;
  initialDomain?: string;
  /** Affects the header copy + primary button label. */
  mode: 'add' | 'edit';
}

export interface AddEmailDialogResult {
  username: string;
  domain: string;
}

/**
 * Dialog twin of the old inline add/edit-email form.
 * Kept intentionally minimal — same visual language as AddReachabilityDialog
 * so Add Email / Add Notification feel like siblings.
 */
@Component({
  selector: 'cmt-add-email-dialog',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    ...HLM_DIALOG_SHELL,
    ...HLM_FORM_FIELD,
    HlmBtnDirective,
    HlmIconComponent,
    HlmInputDirective,
    HlmSelectComponent,
  ],
  template: `
    <hlm-dialog-surface size="md">
      <hlm-dialog-header>
        <hlm-dialog-icon-badge tone="primary">
          <hlm-icon [name]="data.mode === 'edit' ? 'lucidePencil' : 'lucideMail'" size="1.1rem" />
        </hlm-dialog-icon-badge>
        <div hlmDialogHeading>
          <h2 hlmDialogTitle>{{ data.mode === 'edit' ? 'Edit Email Address' : 'Add Email Address' }}</h2>
          <p hlmDialogDescription>Choose a username and domain for this user account.</p>
        </div>
        <button hlmDialogClose (click)="dialogRef.close()">
          <hlm-icon name="lucideX" size="1rem" />
        </button>
      </hlm-dialog-header>

      <section hlmDialogBody>
        <div class="flex flex-wrap items-start gap-2" [formGroup]="form">
          <hlm-form-field class="min-w-[200px] flex-1">
            <label hlmLabel for="email-username">Username</label>
            <input id="email-username" hlmInput formControlName="username" placeholder="john.doe" autocomplete="off" />
          </hlm-form-field>

          <span class="pt-7 text-lg font-semibold text-muted-foreground" aria-hidden="true">&#64;</span>

          <hlm-form-field class="min-w-[220px] flex-[1.3]">
            <label hlmLabel>Domain</label>
            <hlm-select
              [options]="domainOptions"
              [value]="form.controls.domain.value"
              (valueChange)="form.controls.domain.setValue($any($event) || '')"
            />
          </hlm-form-field>
        </div>

        <div class="mt-3 flex items-center gap-2 rounded-lg bg-muted p-2.5 text-sm text-muted-foreground">
          <hlm-icon name="lucideInfo" size="0.9rem" class="shrink-0" />
          <span>Domains are managed centrally. Contact your admin if the needed domain is missing.</span>
        </div>
      </section>

      <footer hlmDialogFooter>
        <button hlmBtn variant="outline" (click)="dialogRef.close()">Cancel</button>
        <button hlmBtn (click)="submit()" [disabled]="form.invalid">
          <hlm-icon [name]="data.mode === 'edit' ? 'lucideSave' : 'lucidePlus'" size="0.9rem" />
          {{ data.mode === 'edit' ? 'Save Changes' : 'Add Email' }}
        </button>
      </footer>
    </hlm-dialog-surface>
  `,
})
export class AddEmailDialogComponent {
  readonly dialogRef = inject(MatDialogRef<AddEmailDialogComponent>);
  readonly data: AddEmailDialogData = inject(MAT_DIALOG_DATA);

  readonly form = new FormGroup({
    username: new FormControl(this.data.initialUsername ?? '', {
      nonNullable: true,
      validators: [Validators.required],
    }),
    domain: new FormControl(this.data.initialDomain ?? (this.data.domains[0] ?? ''), {
      nonNullable: true,
      validators: [Validators.required],
    }),
  });

  readonly domainOptions: HlmSelectOption<string>[] = this.data.domains.map(d => ({
    value: d,
    label: d,
  }));

  submit(): void {
    if (this.form.invalid) return;
    this.dialogRef.close(this.form.getRawValue() as AddEmailDialogResult);
  }
}
